import { useFormState } from "react-final-form";
import { useTranslation } from "react-i18next";
import { shallowEqual } from "react-redux";
import { useSelector } from "src/store";
import { TextField } from "src/ui/shared/form/TextField";

import { selectProvisionings } from "@dashboard/provisioning/store/selectors";
import { Row, SectionDescription } from "@dashboard/shared/styles";
import { useTheme } from "@mui/material";
import { CustomAlert } from "@shared/CustomAlert/CustomAlert";

import {
    CollapsibleProvider,
    CollapsibleSectionDetails,
    CollapsibleSectionSummary,
    CollapsibleSectionTitle,
    useCollapsiblePersistence,
} from "../../shared/components/CollapsibleSection";
import { Provisioning } from "../types";
import { requiredInternetConfigMessage } from "../utils";
import { formatSummaryList } from "../utils/sectionSummary";

import { hasInternetConfig } from "./InternetConfig/InternetConfig.utils";

export const ProductBranding = () => {
    const theme = useTheme();
    const { t, i18n } = useTranslation(["pages"]);
    const { value, onChange } = useCollapsiblePersistence("provisioningPage.collapsedSections", "ProductBranding");

    const { values: parentFormValues } = useFormState<Partial<Provisioning>>();
    const isDisabled = !hasInternetConfig(parentFormValues?.settings?.internetConfig);
    const provisionings = useSelector(selectProvisionings, shallowEqual);

    const brandingLabels = [
        parentFormValues?.settings?.wallpaperUrl ? t("pages:provisioningPage.summaryLabel.wallpaper") : "",
        parentFormValues?.settings?.bootAnimationUrl ? t("pages:provisioningPage.summaryLabel.bootAnimation") : "",
    ].filter((label) => !!label);
    const summary = brandingLabels.length ? formatSummaryList(brandingLabels, i18n.language) : undefined;

    return (
        <CollapsibleProvider initialValue={value} onChange={onChange} disabled={isDisabled}>
            <CollapsibleSectionSummary>
                <CollapsibleSectionTitle summary={summary}>
                    {t("pages:provisioningPage.section.productBranding.title")}
                </CollapsibleSectionTitle>
            </CollapsibleSectionSummary>
            <CollapsibleSectionDetails error={isDisabled ? requiredInternetConfigMessage : undefined}>
                <SectionDescription>{t("pages:provisioningPage.section.productBranding.description")}</SectionDescription>
                <CustomAlert style={{ marginTop: 0, marginBottom: theme.spacing(2) }} severity="info">
                    {t("pages:provisioningPage.alert.productBranding")}
                </CustomAlert>
                <Row style={{ paddingBottom: theme.spacing(2) }}>
                    <TextField
                        name="settings.bootAnimationUrl"
                        label={t("pages:provisioningPage.input.bootAnimationUrl.caption")}
                        fullWidth
                        disabled={!provisionings.length}
                    />
                </Row>
                <Row style={{ paddingBottom: theme.spacing(2) }}>
                    <TextField
                        name="settings.wallpaperUrl"
                        label={t("pages:provisioningPage.input.wallpaperUrl.caption")}
                        fullWidth
                        disabled={!provisionings.length}
                    />
                </Row>
            </CollapsibleSectionDetails>
        </CollapsibleProvider>
    );
};
